import { RangeHorizon } from '@toggle/chart';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useExplore } from '~/hooks/use-explore/useExplore';
import { useInsightsFullDrawer } from '~/hooks/use-insights-full-drawer/useInsightsFullDrawer';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { queryKeys } from '~/routes/query-params';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { RangeHorizonValue } from '~/views/insights/components/insights-drawer/insights-left-section/InsightsLeftSection';

import * as S from './ViewAllInsightsSection.styles';

interface ViewAllInsightsSectionProps {
  entityName: string;
  entityTag: string;
  insightId: string;
}

export const ViewAllInsightsSection = ({
  entityName,
  entityTag,
  insightId,
}: ViewAllInsightsSectionProps) => {
  const { t } = useTranslation(['insights']);
  const { openDrawer } = useInsightsFullDrawer();
  const { trackInsightsDrawerOpen } = usePosthogEvents();
  const { params } = useQueryParams();

  const { data: insightsData, isLoading: isInsightsLoading } = useExplore({
    entityTags: [entityTag],
    filters: {
      entity_tag: {
        values: [
          {
            tags: [entityTag],
          },
        ],
      },
      period: { values: [RangeHorizonValue[RangeHorizon.OneMonth]] },
    },
    sortBy: undefined,
    combined: true,
  });

  if (isInsightsLoading) {
    return <S.StyledLoader />;
  }

  if (!insightsData) {
    return null;
  }

  const availableInsights =
    insightsData.pages?.[0]?.entities[0]?.insights ?? [];
  const additionalInsightsCount = availableInsights.length;

  const trackingProperties = params[queryKeys.source];
  let parsedTrackingProperties: Record<string, string> = {};

  try {
    if (trackingProperties) {
      const parsed = JSON.parse(trackingProperties);

      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        parsedTrackingProperties = parsed;
      }
    }
  } catch {}

  const { source, ...rest } = parsedTrackingProperties;

  if (additionalInsightsCount > 1) {
    return (
      <S.Root>
        <S.TitleLabel>
          {t('insights:viewAllSection.whatElseIsMoving', {
            name: entityName,
          })}
        </S.TitleLabel>
        <S.DescriptionLabel>
          {t('insights:viewAllSection.moreInsights', {
            count: additionalInsightsCount - 1,
          })}
        </S.DescriptionLabel>
        <S.StyledButton
          variant="primary"
          label={t('insights:viewAllSection.viewAll')}
          onClick={() => {
            trackInsightsDrawerOpen(
              source as PosthogTrackingSource,
              'full',
              rest
            );
            openDrawer({ entityTag, insightId });
          }}
        />
      </S.Root>
    );
  }

  return null;
};
